/**
 * BoardNoteEditor - Markdown editor for board notes
 *
 * Auto-save is disabled to prevent rapid RAG updates.
 * Users must use the Save button to persist changes and sync to RAG.
 */

import { useState, useEffect } from 'react';
import { clsx } from 'clsx';
import { Save, X, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import MarkdownEditor from '@components/ui/MarkdownEditor';
import { useUIStore } from '@stores/uiStore';
import type { BoardNote } from '@services/api/boardNotes';

interface BoardNoteEditorProps {
  note: BoardNote;
  isSaving: boolean;
  onSave: (data: { title?: string; content?: string }) => Promise<void>;
  onClose: () => void;
}

export default function BoardNoteEditor({
  note,
  isSaving,
  onSave,
  onClose,
}: BoardNoteEditorProps) {
  const { t } = useTranslation();
  const [localTitle, setLocalTitle] = useState(note.title);
  const [localContent, setLocalContent] = useState(note.content || '');
  const [hasChanges, setHasChanges] = useState(false);
  const { resolvedTheme } = useUIStore();

  // Track if content has changed
  useEffect(() => {
    const titleChanged = localTitle !== note.title;
    const contentChanged = localContent !== (note.content || '');
    setHasChanges(titleChanged || contentChanged);
  }, [localTitle, localContent, note.title, note.content]);

  // Reset local state when note changes
  useEffect(() => {
    setLocalTitle(note.title);
    setLocalContent(note.content || '');
  }, [note.id, note.title, note.content]);

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLocalTitle(e.target.value);
  };

  const handleContentChange = (value: string | undefined) => {
    setLocalContent(value || '');
  };

  const handleManualSave = async () => {
    if (!hasChanges || isSaving) return;

    try {
      const data: { title?: string; content?: string } = {};
      if (localTitle !== note.title) data.title = localTitle;
      if (localContent !== (note.content || '')) data.content = localContent;
      await onSave(data);
    } catch (error) {
      console.error('Failed to save note:', error);
    }
  };

  // Keyboard shortcut: Cmd/Ctrl+S to save
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 's') {
        e.preventDefault();
        void handleManualSave();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [hasChanges, isSaving, localTitle, localContent, note.title, note.content]);

  return (
    <div className="flex h-full flex-col">
      {/* Editor Header */}
      <div
        className={clsx(
          'flex shrink-0 items-center justify-between gap-2',
          'border-b border-neutral-200 dark:border-neutral-700',
          'px-4 py-2'
        )}
      >
        <div className="flex items-center gap-2">
          <button
            onClick={onClose}
            className={clsx(
              'rounded p-1.5',
              'text-neutral-500 hover:text-neutral-700',
              'dark:text-neutral-400 dark:hover:text-neutral-200',
              'hover:bg-neutral-100 dark:hover:bg-neutral-800',
              'transition-colors'
            )}
            title={t('board.backToNotes')}
          >
            <X className="h-4 w-4" />
          </button>
          <span className="text-sm text-neutral-500 dark:text-neutral-400">
            {hasChanges ? (
              <span className="text-amber-500 dark:text-amber-400">{t('board.unsavedChanges')}</span>
            ) : (
              t('board.savedStatus')
            )}
          </span>
        </div>

        <button
          onClick={() => void handleManualSave()}
          disabled={!hasChanges || isSaving}
          className={clsx(
            'flex items-center gap-1.5 rounded px-3 py-1.5',
            'text-sm font-medium',
            hasChanges && !isSaving
              ? 'bg-primary-600 text-white hover:bg-primary-700'
              : 'bg-neutral-200 text-neutral-400 dark:bg-neutral-700 dark:text-neutral-500',
            'disabled:cursor-not-allowed',
            'transition-colors'
          )}
        >
          {isSaving ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Save className="h-4 w-4" />
          )}
          {t('common.save')}
        </button>
      </div>

      {/* Title input */}
      <div className="shrink-0 border-b border-neutral-200 px-4 py-3 dark:border-neutral-700">
        <input
          type="text"
          value={localTitle}
          onChange={handleTitleChange}
          placeholder={t('board.noteTitlePlaceholder')}
          className={clsx(
            'w-full bg-transparent',
            'text-lg font-medium',
            'text-neutral-900 dark:text-neutral-100',
            'placeholder-neutral-400 dark:placeholder-neutral-500',
            'outline-none'
          )}
        />
      </div>

      {/* Markdown Editor */}
      <div className="flex-1 overflow-hidden">
        <MarkdownEditor
          value={localContent}
          onChange={handleContentChange}
          placeholder={t('board.noteContentPlaceholder')}
          dark={resolvedTheme === 'dark'}
        />
      </div>
    </div>
  );
}
